import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { Actions } from '@src/app/shared/backend/auth/Action';
import { SurveyDetailsResponse, SurveysService } from '@src/app/shared/backend/clients';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';
import { DialogReasonComponent } from '@src/app/shared/components/dialog-reason/dialog-reason.component';
import { PermissionService } from '@src/app/shared/services/permission.service';
import { ToasterService } from '@src/app/shared/components/toaster/toaster.service';

@Component({
    selector: 'app-view-survey',
    templateUrl: './view-survey.component.html',
    styleUrl: './view-survey.component.scss',
    host: {
        class: 'app-view-surveys'
    },
    providers: [ConfirmationService],
    standalone: false
})
export class ViewSurveyComponent extends BaseViewComponent implements OnInit, OnDestroy {

  private readonly destroyedComponent$ = new Subject<void>();

  @ViewChild('dialog') dialog: DialogReasonComponent | undefined;

  showPublishNotice = true;
  isPublishing = false;

  delete = false;
  allowModifySurvey = false;
  surveyId = 0;
  survey: SurveyDetailsResponse | undefined;

  stateOptions: any[];
  stateInitialValue: boolean | undefined;
  stateNewValue: boolean | undefined;

  sanitizedConfirmMessage: SafeHtml | undefined;

  constructor(
    private permissionService: PermissionService,
    private router: Router,
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private translate: TranslateService,
    private toasterService: ToasterService,
    private confirmationService: ConfirmationService,
    private surveysService: SurveysService
  ) {
    super();
    this.updateStateOptions();
  }

  ngOnInit(): void {
    this.translate.onLangChange
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe(() => {
        this.updateStateOptions();
      });

    this.init();
  }

  async init(): Promise<void> {
    this.route.params
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe(params => {
        this.surveyId = Number(params['surveyId']);

        if (this.surveyId > 0) {
          this.get();
        }
      });

    this.delete = await this.permissionService.hasActionPermissions(Actions.delete);
    this.allowModifySurvey = await this.permissionService.hasActionPermissions(Actions.modify_surveys);
  }

  override async get(): Promise<void> {
    try {
      this.survey = await firstValueFrom(this.surveysService.apiSurveysDetailsIdGet(this.surveyId));
      this.initStateSwitch();
    } catch (error) {
      console.log('Error fetching survey data:', error);
    }
  }

  goToEditSurvey(): void {
    if (this.survey?.isPublished) {
      return;
    }

    this.router.navigate([`/surveys/${this.surveyId}/edit`]);
  }

  async onDelete(doit: boolean): Promise<void> {
    if (!doit) {
      return;
    }

    await firstValueFrom(this.surveysService.apiSurveysDeleteIdDelete(this.surveyId));
    this.router.navigate(['/surveys']);
  }

  cancelPublishNotice(): void {
    this.showPublishNotice = false;
  }

  async publishSurvey(): Promise<void> {
    if (this.isPublishing) {
      return;
    }

    this.isPublishing = true;

    try {
      await firstValueFrom(this.surveysService.apiSurveysPublicationIdPatch(this.surveyId));
      await this.get();

      this.toasterService.show(
        'success',
        this.translate.instant('SURVEYS_SINGLE_VIEW.TOAST_SUCCESS_TITLE_SURVEY_PUBLISH_SUCCESS'),
        this.translate.instant('SURVEYS_SINGLE_VIEW.TOAST_SUCCESS_MESSAGE_SURVEY_PUBLISH_SUCCESS')
      );

      this.showPublishNotice = false;
    } catch (error) {
      console.log('Error publishing survey:', error);

      this.toasterService.show(
        'error',
        this.translate.instant('SURVEYS_SINGLE_VIEW.TOAST_ERROR_TITLE_SURVEY_PUBLISH_FAILED'),
        this.translate.instant('SURVEYS_SINGLE_VIEW.TOAST_ERROR_MESSAGE_SURVEY_PUBLISH_FAILED')
      );
    } finally {
      this.isPublishing = false;
    }
  }

  async activate(): Promise<void> {
    await firstValueFrom(this.surveysService.apiSurveysIdActivatePut(this.surveyId));
    await this.get();
  }

  async deactivate(): Promise<void> {
    await firstValueFrom(this.surveysService.apiSurveysIdDeactivatePut(this.surveyId));
    await this.get();
  }

  private updateStateOptions(): void {
    this.stateOptions = [
      {
        label: this.translate.instant('BOOLEANS.ACTIVATE_SWITCH'),
        displayLabel: this.translate.instant('BOOLEANS.ACTIVATE_SWITCH'),
        value: true
      },
      {
        label: this.translate.instant('BOOLEANS.DEACTIVATE_SWITCH'),
        displayLabel: this.translate.instant('BOOLEANS.DEACTIVATE_SWITCH'),
        value: false
      }
    ];
  }

  initStateSwitch(): void {
    this.stateInitialValue = this.survey?.isActive;
    this.updateConfirmMessage(this.stateInitialValue);
  }

  onStateChange(event: any): void {
    this.stateNewValue = event.value;

    const selectedLabel = this.stateOptions.find(option => option.value === this.stateNewValue)?.label.toLowerCase();

    const translatedMessage = this.translate.instant('MODALBOXES.CONFIRM_STATE_CHANGE', { state: selectedLabel });

    this.sanitizedConfirmMessage = this.sanitizer.bypassSecurityTrustHtml(translatedMessage);

    this.confirmationService.confirm({
      key: 'confirm-state-change-dialog',
      header: this.translate.instant('MODALBOXES.TITLE_CONFIRM_STATE_CHANGE')
    });
  }

  async onConfirmStateChange(): Promise<void> {
    if (this.stateNewValue === false) {
      await this.deactivate();
    }

    if (this.stateNewValue === true) {
      await this.activate();
    }

    this.confirmationService.close();
  }

  onCancelStateChange(): void {
    this.stateInitialValue = this.survey?.isActive;
    this.confirmationService.close();
  }

  takeSurvey(): void {
    if (!this.survey?.isPublished) {
      return;
    }

    this.router.navigate([`/surveys/${this.surveyId}/take-survey`]);
  }

  updateConfirmMessage(state: boolean | undefined): void {
    const selectedLabel = this.stateOptions.find(option => option.value === state)?.label.toLowerCase();

    const translatedMessage = this.translate.instant('MODALBOXES.CONFIRM_STATE_CHANGE', { state: selectedLabel });

    this.sanitizedConfirmMessage = this.sanitizer.bypassSecurityTrustHtml(translatedMessage);
  }

  ngOnDestroy(): void {
    this.destroyedComponent$.next();
    this.destroyedComponent$.complete();
  }
}
